import type { Rail } from "../level";
import { LIFT_SPEED } from "../sim/tuning";
import { sampleCurve, sideways, type CurveSample, type Point3 } from "./curve";
import { checkColor, rad, type PieceKind } from "./types";

// Scenery (#156): rides, buildings, park furniture and the indoor kit (#181:
// trusses, catwalks, factory windows, lamps, pipes, murals, pallets; #246's
// control booth, plywood sheets and light shafts) that are drawn but
// never skated. A scenery piece compiles to no primitives and no rails, so
// the sim, the reach bot, playcheck and the flow report never see it; the
// skateable parts of a ride (a coaster's track to grind, a stall's counter
// as a ledge) are ordinary pieces placed beside it. The exceptions are
// in `SCENERY_RAILS`: a drained flume's walls (#170) are grindable ledges,
// and a coaster's track (#189) is two long grinds, one per running rail (#231), each compiled here from the
// same path and sizes its mesh is drawn from, so the two can't drift apart. Each type's mesh is its
// own file under render/scenery/ (SCENERY_MESHES). Anything that moves (the
// wheel, the carousel) turns on the render clock, never the sim's.

/**
 * What a scenery piece is drawn as. `coaster`, `flume`, `lights`, `fence`,
 * `pipes` and `duct` follow a `path`; the rest stand at (x, z), turned by `rot`.
 */
export type SceneryType =
  | "ferris-wheel"
  | "carousel"
  | "coaster"
  | "flume"
  | "slide"
  | "stall"
  | "food"
  | "burger"
  | "castle"
  | "tent"
  | "lamp"
  | "lights"
  | "fence"
  | "bench"
  | "umbrella"
  | "balloons"
  | "tree"
  | "paving"
  | "building"
  // The indoor kit (#181).
  | "truss"
  | "beam"
  | "catwalk"
  | "window"
  | "pendant"
  | "pipes"
  | "duct"
  | "mural"
  | "pallet"
  | "crate"
  // The Depot's (#246).
  | "booth"
  | "plywood"
  | "shaft";

export const SCENERY_TYPES: readonly SceneryType[] = [
  "ferris-wheel",
  "carousel",
  "coaster",
  "flume",
  "slide",
  "stall",
  "food",
  "burger",
  "castle",
  "tent",
  "lamp",
  "lights",
  "fence",
  "bench",
  "umbrella",
  "balloons",
  "tree",
  "paving",
  "building",
  "truss",
  "beam",
  "catwalk",
  "window",
  "pendant",
  "pipes",
  "duct",
  "mural",
  "pallet",
  "crate",
  "booth",
  "plywood",
  "shaft",
];

/** The types drawn along a path of points rather than at one spot. */
export const PATH_SCENERY: readonly SceneryType[] = ["coaster", "flume", "lights", "fence", "pipes", "duct"];

/** The types that can carry a shop sign (#233): a stand's or a building's name board. */
export const SIGNED_SCENERY: readonly SceneryType[] = ["stall", "food", "burger", "building"];

/**
 * A made-up business's name board on a stand or a building (#233): its name
 * big, and what it sells under it. Invented names only, no real brands, and
 * no name twice on one map (#167).
 */
export interface ShopSign {
  name: string;
  line?: string;
}

/** What a sign's words may be: capitals, digits, spaces and a little punctuation, short enough to read. */
export const SIGN_TEXT = /^[A-Z0-9 &'.!-]{1,26}$/;

/** A point on a path: world x, z and how high it runs there (m). */
export interface PathPoint {
  x: number;
  z: number;
  y: number;
}

/** A compiled scenery piece (rot in radians, its path in world coordinates). */
export interface Scenery {
  type: SceneryType;
  x: number;
  z: number;
  rot: number;
  /** Scales the whole thing (1 when left out). */
  size: number;
  /** How long it is along its u axis (m), for the types that take one. */
  len?: number;
  /** How wide it is across, along w (m), for the types that take one. */
  wide?: number;
  /** How tall (m), for the types that take one. */
  h?: number;
  /** Its main color, for the types that take one. */
  color?: string;
  /** Its name board, for the types in `SIGNED_SCENERY`. */
  sign?: ShopSign;
  path?: PathPoint[];
}

/**
 * A scenery piece in a map file. A path's points are offsets in metres from
 * (x, z), along the world's x and z (not turned by `rot`), with `y` how high
 * the path runs there.
 */
export interface SceneryPiece {
  kind: "scenery";
  type: SceneryType;
  x: number;
  z: number;
  rot?: number;
  size?: number;
  len?: number;
  wide?: number;
  h?: number;
  color?: string;
  sign?: ShopSign;
  path?: { x: number; z: number; y?: number }[];
}

/** A flume's wall stands this far outside the trough's half-width (m), its centre line. */
export const FLUME_WALL_OUT = 0.08;
/** How high a flume wall's top stands off the trough's floor (the path), square to the floor (m). */
export const FLUME_WALL_TOP = 0.75;
/** A flume with water shallower than this (m) is drained: a chute you skate. */
export const FLUME_DRY = 0.1;

/** Whether a flume is drained (a chute you skate). Water runs 0.3 m deep when `h` is left out. */
export const flumeDrained = (s: Pick<Scenery, "h">): boolean => (s.h ?? 0.3) < FLUME_DRY;

/**
 * A drained flume's grind lines (#170): the top of each wall, one ledge per
 * path segment and side, square to that segment's floor the way the wall is
 * drawn (`render/scenery/flume.ts`), so a wall up a slope tips back with it.
 */
export function flumeLedges(s: Scenery): Rail[] {
  if (s.type !== "flume" || !s.path || !flumeDrained(s)) return [];
  const out = (s.wide ?? 1.6) / 2 + FLUME_WALL_OUT;
  const rails: Rail[] = [];
  for (let i = 0; i < s.path.length - 1; i++) {
    const a = s.path[i]!;
    const b = s.path[i + 1]!;
    const len = Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
    const run = Math.hypot(b.x - a.x, b.z - a.z);
    if (run < 1e-6) continue;
    // Level sideways (dir x up), then up square to the floor (side x dir).
    const [dx, dy, dz] = [(b.x - a.x) / len, (b.y - a.y) / len, (b.z - a.z) / len];
    const [sx, sz] = [-dz / Math.hypot(dx, dz), dx / Math.hypot(dx, dz)];
    const up = { x: -sz * dy, y: sz * dx - sx * dz, z: sx * dy };
    for (const side of [out, -out]) {
      const at = (p: PathPoint) => ({ x: p.x + sx * side + up.x * FLUME_WALL_TOP, z: p.z + sz * side + up.z * FLUME_WALL_TOP });
      const y = (p: PathPoint) => p.y + up.y * FLUME_WALL_TOP;
      rails.push({ a: at(a), b: at(b), y: y(a), ...(b.y === a.y ? {} : { yb: y(b) }), kind: "ledge" });
    }
  }
  return rails;
}

/** A closed coaster's ride ends this far (m) short of where it started, so the grind runs once round, not forever. */
export const TRACK_END_GAP = 0.3;
/** How far each of a coaster's two running rails stands out from its path, either side (m). */
export const COASTER_GAUGE = 0.55;
/** A coaster's track is sampled along its curve every this many metres (or a little less): the mesh's ties and the grind lines' joints. */
export const COASTER_STEP = 1;

/** Whether a path closes on itself: its last point is its first again. */
export function pathClosed(path: readonly PathPoint[]): boolean {
  const first = path[0]!;
  const end = path[path.length - 1]!;
  return path.length > 3 && Math.hypot(end.x - first.x, end.z - first.z) < 1e-6 && Math.abs(end.y - first.y) < 1e-6;
}

/**
 * A coaster's track as samples along its curve (#231): the smooth curve
 * through its path (`prims/curve.ts`) every `COASTER_STEP`, the way it runs
 * at each. A path that closes on itself is a closed curve, smooth through
 * the station too (its last sample is its first again). The mesh draws
 * its ties, spine and rails from these.
 */
export function coasterSamples(path: readonly PathPoint[]): CurveSample[] {
  return pathClosed(path) ? sampleCurve(path.slice(0, -1), COASTER_STEP, true) : sampleCurve(path, COASTER_STEP);
}

/**
 * A coaster's two running rails (#231): its samples (`coasterSamples`)
 * offset level sideways by `COASTER_GAUGE` to the left and right of travel.
 * The mesh draws its rails through these points and the grind lines join
 * them up, so the two can't part on a curve.
 */
export function coasterLines(path: readonly PathPoint[]): { left: PathPoint[]; right: PathPoint[] } {
  const left: PathPoint[] = [];
  const right: PathPoint[] = [];
  let side: Point3 | undefined;
  for (const { at, dir } of coasterSamples(path)) {
    side = sideways(dir, side);
    left.push({ x: at.x - side.x * COASTER_GAUGE, z: at.z - side.z * COASTER_GAUGE, y: at.y });
    right.push({ x: at.x + side.x * COASTER_GAUGE, z: at.z + side.z * COASTER_GAUGE, y: at.y });
  }
  return { left, right };
}

/**
 * A coaster's track as two long grinds (#189, #231): one along each running
 * rail's top (`coasterLines`, at the path's `y`), a rail per sample, all
 * `joined`, so a grind carries on round the whole ride on the side you got
 * on. They're `track` rails: drawn by the coaster, and allowed outside the
 * fence. The climb from the track's lowest point to its highest is the
 * chain lift (`lift`). A track whose path closes on itself ends just short
 * of its start: you ride it once, from wherever you got on, to there.
 */
export function coasterRails(s: Scenery): Rail[] {
  if (s.type !== "coaster" || !s.path) return [];
  const closed = pathClosed(s.path);
  const rails: Rail[] = [];
  for (const pts of Object.values(coasterLines(s.path))) {
    const low = pts.reduce((k, p, i) => (p.y < pts[k]!.y ? i : k), 0);
    const high = pts.reduce((k, p, i) => (p.y > pts[k]!.y ? i : k), 0);
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i]!;
      let b = pts[i + 1]!;
      const run = Math.hypot(b.x - a.x, b.z - a.z);
      if (run < 1e-6) continue;
      if (closed && i === pts.length - 2) {
        const k = Math.max(0, run - TRACK_END_GAP) / run;
        b = { x: a.x + (b.x - a.x) * k, z: a.z + (b.z - a.z) * k, y: a.y + (b.y - a.y) * k };
      }
      // Up the lift hill: segments between the lowest point and the highest (going round, if it closes).
      const onLift = b.y > a.y && (low <= high ? i >= low && i < high : i >= low || i < high);
      rails.push({ a: { x: a.x, z: a.z }, b: { x: b.x, z: b.z }, y: a.y, ...(b.y === a.y ? {} : { yb: b.y }), kind: "rail", joined: true, track: true, ...(onLift ? { lift: LIFT_SPEED } : {}) });
    }
  }
  return rails;
}

/** The scenery types that are partly skateable, and the rails each compiles to. */
export const SCENERY_RAILS: { readonly [K in SceneryType]?: (s: Scenery) => Rail[] } = {
  flume: flumeLedges,
  coaster: coasterRails,
};

/** How far outside the fence scenery may stand, as a multiple of `bounds`: rides behind the walls are scenery too. */
const SCENERY_REACH = 2;

export const SCENERY_PIECE: PieceKind<SceneryPiece> = {
  fields: ["type", "x", "z", "rot", "size", "len", "wide", "h", "color", "sign", "path"],
  placed: false,
  check(c, o, path, bounds) {
    const far = { min: -SCENERY_REACH * bounds, max: SCENERY_REACH * bounds };
    if (!SCENERY_TYPES.includes(o.type as SceneryType)) c.fail(`${path}.type`, `expected one of ${SCENERY_TYPES.join(", ")}`);
    c.num(o, "x", path, far);
    c.num(o, "z", path, far);
    c.num(o, "rot", path, { min: -360, max: 360, optional: true });
    c.num(o, "size", path, { min: 0.2, max: 4, optional: true });
    c.num(o, "len", path, { min: 0.2, max: 4 * bounds, optional: true });
    c.num(o, "wide", path, { min: 0.2, max: 4 * bounds, optional: true });
    c.num(o, "h", path, { min: 0, max: 60, optional: true });
    checkColor(c, o, path);
    if (o.sign !== undefined) {
      if (!SIGNED_SCENERY.includes(o.type as SceneryType)) c.fail(`${path}.sign`, `only ${SIGNED_SCENERY.join(", ")} take a sign`);
      const sign = c.obj(o.sign, `${path}.sign`);
      if (sign) {
        c.only(sign, ["name", "line"], `${path}.sign`);
        c.str(sign, "name", `${path}.sign`, SIGN_TEXT);
        if (sign.line !== undefined) c.str(sign, "line", `${path}.sign`, SIGN_TEXT);
      }
    }
    const wantsPath = PATH_SCENERY.includes(o.type as SceneryType);
    if (o.path === undefined) {
      if (wantsPath) c.fail(`${path}.path`, `a ${String(o.type)} needs a path of at least 2 points`);
      return;
    }
    if (!wantsPath) {
      c.fail(`${path}.path`, `only ${PATH_SCENERY.join(", ")} take a path`);
      return;
    }
    if (!Array.isArray(o.path) || o.path.length < 2) {
      c.fail(`${path}.path`, "expected a list of at least 2 points");
      return;
    }
    o.path.forEach((v, i) => {
      const pt = c.obj(v, `${path}.path[${i}]`);
      if (!pt) return;
      c.only(pt, ["x", "z", "y"], `${path}.path[${i}]`);
      c.num(pt, "x", `${path}.path[${i}]`, { min: -4 * bounds, max: 4 * bounds });
      c.num(pt, "z", `${path}.path[${i}]`, { min: -4 * bounds, max: 4 * bounds });
      c.num(pt, "y", `${path}.path[${i}]`, { min: 0, max: 60, optional: true });
    });
  },
  compile(p, out) {
    const s: Scenery = { type: p.type, x: p.x, z: p.z, rot: rad(p.rot), size: p.size ?? 1 };
    if (p.len !== undefined) s.len = p.len;
    if (p.wide !== undefined) s.wide = p.wide;
    if (p.h !== undefined) s.h = p.h;
    if (p.color !== undefined) s.color = p.color;
    if (p.sign) s.sign = { name: p.sign.name, ...(p.sign.line ? { line: p.sign.line } : {}) };
    if (p.path) s.path = p.path.map((pt) => ({ x: p.x + pt.x, z: p.z + pt.z, y: pt.y ?? 0 }));
    out.scenery.push(s);
    out.rails.push(...(SCENERY_RAILS[s.type]?.(s) ?? []));
  },
};
